import React, { useEffect, useLayoutEffect, useState } from 'react';

import { Box, Button, Card, Stack, Typography, TypographySystem } from '@mui/joy';

import { track } from '../../../services/analytics';
import {
  EVENT_TUTORIAL,
  EVNT_tutorial_screen_main_cta_clicked,
  EVNT_tutorial_screen_secondary_cta_clicked,
  PGV_tutorial_screen_shown,
} from '../../../services/analytics/events';
import { useUnratedOnboardingSteps } from '../steps/useUnratedOnboardingSteps';
import { useOnboardingTour } from '../tour';
import {
  useOnboardingDismissalStore,
  useShouldShowOnboarding,
} from '../useOnboardingDismissalStore';

/** Figma node 1914:98737 — upward-pointing white triangle, 25.11 × 18.75. */
const LauncherArrow: React.FC = () => (
  <svg
    width='25'
    height='19'
    viewBox='0 0 25.1147 18.75'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    style={{ display: 'block' }}
  >
    <path
      d='M12.5574 0L25.1147 18.75H0L12.5574 0Z'
      fill='var(--joy-palette-background-surface, #ffffff)'
    />
  </svg>
);

const MOUNT_DELAY_MS = 800;
const ONBOARDING_KEY = 'unrated';
const ALWAYS_SHOW_FOR_TESTING = process.env.NODE_ENV === 'development';
const FILTER_BUTTON_SELECTOR = '[data-tour="unrated-filter-button"]';
// Figma node 1914:98736: card 420×233, arrow polygon at x=391 (29px wide) → arrow centre 405.5px from left, 14.5px from right.
const CARD_WIDTH = 420;
const ARROW_WIDTH = 25;
const ARROW_HEIGHT = 19;
const ARROW_CENTER_FROM_RIGHT = 14;
// Gap between Filter button bottom and the arrow tip.
const ANCHOR_GAP_PX = 6;

const LAUNCHER_EVENT_PROPS = {
  platform_page: 'unrated',
  feature_name: 'unrated-projects',
  tutorial_step_number: 'Launcher',
  tutorial_screen_header_text: 'Explore all projects, not just the rated ones',
};

type AnchorRect = { top: number; left: number };

const computeAnchorRect = (): AnchorRect | null => {
  const filterBtn = document.querySelector(FILTER_BUTTON_SELECTOR);
  if (!(filterBtn instanceof HTMLElement)) return null;
  const r = filterBtn.getBoundingClientRect();
  // Position the wrapper so the triangle tip lines up under the Filter button's
  // horizontal centre. The wrapper's left edge sits at (filter centre) minus
  // (CARD_WIDTH - ARROW_CENTER_FROM_RIGHT - ARROW_WIDTH/2).
  const filterCx = r.left + r.width / 2;
  const arrowCenterFromLeft = CARD_WIDTH - ARROW_CENTER_FROM_RIGHT - ARROW_WIDTH / 2;
  const top = r.bottom + ANCHOR_GAP_PX;
  const left = Math.max(16, filterCx - arrowCenterFromLeft);
  return { top, left };
};

export const UnratedOnboardingLauncher: React.FC = () => {
  const steps = useUnratedOnboardingSteps();
  // Per-step tour events inherit this tour's identity (platform_page /
  // feature_name); the runner overlays tutorial_step_number + header text per
  // step. Kept in sync with LAUNCHER_EVENT_PROPS so launcher + step events
  // can't drift.
  const { canShow, start: startTour } = useOnboardingTour(
    steps,
    {
      platform_page: 'unrated',
      feature_name: 'unrated-projects',
    },
    { hideBack: true, popoverWidth: 420, lockScroll: true }
  );
  const storageAllowsShowing = useShouldShowOnboarding(ONBOARDING_KEY);
  const shouldShowFromStorage = ALWAYS_SHOW_FOR_TESTING || storageAllowsShowing;
  const markShown = useOnboardingDismissalStore((s) => s.markShown);
  const recordRemindLater = useOnboardingDismissalStore((s) => s.recordRemindLater);
  const markCompleted = useOnboardingDismissalStore((s) => s.markCompleted);
  const [visible, setVisible] = useState(false);
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  useEffect(() => {
    if (!shouldShowFromStorage) return;
    const timer = window.setTimeout(() => {
      if (!ALWAYS_SHOW_FOR_TESTING) markShown(ONBOARDING_KEY);
      setVisible(true);
    }, MOUNT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [shouldShowFromStorage, markShown]);

  useLayoutEffect(() => {
    if (!visible || !canShow) return;
    const update = () => setAnchor(computeAnchorRect());
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visible, canShow]);

  useEffect(() => {
    if (canShow && shouldShowFromStorage && visible && anchor)
      track(PGV_tutorial_screen_shown, EVENT_TUTORIAL, LAUNCHER_EVENT_PROPS);
  }, [canShow, shouldShowFromStorage, visible, anchor]);

  if (!canShow || !shouldShowFromStorage || !visible || !anchor) return null;

  const start = () => {
    track(EVNT_tutorial_screen_main_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      button_text: 'Start the 1 min tour',
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) markCompleted(ONBOARDING_KEY);
    // The tour runner awaits step 1's beforeActions (open-drawer, expand-
    // accordion) before driving — both are registered as React-side tour
    // actions, so no DOM clicks needed here.
    startTour();
  };
  const remindLater = () => {
    track(EVNT_tutorial_screen_secondary_cta_clicked, EVENT_TUTORIAL, {
      ...LAUNCHER_EVENT_PROPS,
      button_text: 'Remind me later',
    });
    setVisible(false);
    if (!ALWAYS_SHOW_FOR_TESTING) recordRemindLater(ONBOARDING_KEY);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: anchor.top,
        left: anchor.left,
        width: CARD_WIDTH,
        // Sit BELOW MUI Joy's Drawer modal layer (default ~1300) so that the
        // filter drawer opens on top of the launcher when the user clicks
        // Start tour. Still well above page content.
        zIndex: 1200,
        animation: 'creditRatingsLauncherSlideIn 0.45s cubic-bezier(0.2, 0.8, 0.2, 1)',
        '@keyframes creditRatingsLauncherSlideIn': {
          from: { transform: 'translateY(-8px)', opacity: 0 },
          to: { transform: 'translateY(0)', opacity: 1 },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: -ARROW_HEIGHT,
          right: ARROW_CENTER_FROM_RIGHT - ARROW_WIDTH / 2,
          width: ARROW_WIDTH,
          height: ARROW_HEIGHT,
          // Casts the same soft drop-shadow up from the triangle as the card body,
          // so the tail blends into the popover's elevation rather than feeling detached.
          filter: 'drop-shadow(0 -2px 6px rgba(21,21,21,0.06))',
        }}
      >
        <LauncherArrow />
      </Box>
      <Card
        sx={(t) => ({
          // No border in the Figma design tokens — only background/surface + shadow-lg.
          border: 'none',
          width: '100%',
          p: '20px',
          gap: '16px',
          borderRadius: '10px',
          boxShadow: 'lg',
          bgcolor: t.palette.background.surface,
        })}
      >
        <Typography
          level={'sub1' as keyof TypographySystem}
          sx={(t) => ({ color: t.palette.text.primary })}
        >
          Explore all projects, not just the rated ones
        </Typography>
        <Typography level='body-md' sx={(t) => ({ color: t.palette.text.primary })}>
          We&rsquo;ve added thousands of unrated projects to our catalog. Here&rsquo;s how to find
          the ones that matter to you.
        </Typography>

        <Stack direction='row' gap='12px' justifyContent='flex-end'>
          <Button variant='outlined' color='neutral' size='sm' onClick={remindLater}>
            Remind me later
          </Button>
          <Button variant='solid' color='primary' size='sm' onClick={start}>
            Start the 1 min tour
          </Button>
        </Stack>
      </Card>
    </Box>
  );
};
